import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MEMBERSHIP_CATEGORIES } from '../../constants';
import { CheckIcon, StarIcon } from '@heroicons/react/24/solid';
import { UserGroupIcon, AcademicCapIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';

const MembershipCategoriesPage: React.FC = () => {
  const navigate = useNavigate();

  const handleCategorySelect = (categoryId: string) => {
    navigate(`/membership/application?category=${categoryId}`);
  };

  const getCategoryIcon = (categoryId: string) => {
    switch (categoryId) {
      case 'student':
        return <AcademicCapIcon className="w-8 h-8" />;
      case 'affiliate':
        return <UserGroupIcon className="w-8 h-8" />;
      case 'graduate':
        return <AcademicCapIcon className="w-8 h-8" />;
      case 'associate':
        return <UserGroupIcon className="w-8 h-8" />;
      case 'professional':
        return <BuildingOfficeIcon className="w-8 h-8" />;
      default:
        return <UserGroupIcon className="w-8 h-8" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container-max section-padding">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Membership Categories
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the membership category that best fits your professional needs and goals
            </p>
          </div>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="section-padding">
        <div className="container-max">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {MEMBERSHIP_CATEGORIES.map((category) => (
              <div
                key={category.id}
                onClick={() => handleCategorySelect(category.id)}
                className={`relative card transition-all duration-300 hover:shadow-xl cursor-pointer ${
                  category.isPopular 
                    ? 'ring-2 ring-primary-500 transform scale-105' 
                    : 'hover:transform hover:scale-105'
                }`}
              >
                {category.isPopular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-primary-500 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center">
                      <StarIcon className="w-4 h-4 mr-1" />
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="text-center mb-6">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                    category.isPopular ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {getCategoryIcon(category.id)}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {category.name}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {category.description}
                  </p>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900 mb-2">
                      {category.currency} {category.price.toLocaleString()}
                      <span className="text-lg font-normal text-gray-500"> to join</span>
                    </div>
                    {category.renewalPrice && (
                      <div className="text-sm text-gray-600">
                        Renewal: {category.currency} {category.renewalPrice.toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3 mb-8">
                  {category.features.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <CheckIcon className="w-5 h-5 text-primary-500 mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <button 
                  onClick={() => handleCategorySelect(category.id)}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors duration-200 ${
                    category.isPopular
                      ? 'btn-primary'
                      : 'btn-secondary'
                  }`}
                >
                  Choose {category.name}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Detailed Category Descriptions */}
      <div className="bg-white section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Membership Category Details
            </h2>
            <p className="text-xl text-gray-600">
              Learn more about each membership level and find the right fit for you
            </p>
          </div>

          <div className="space-y-12">
            {MEMBERSHIP_CATEGORIES.map((category, index) => (
              <div key={category.id} className="border border-gray-200 rounded-xl p-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-1">
                    <div className="flex items-center mb-4">
                      <div className={`p-3 rounded-lg mr-4 ${
                        category.isPopular ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {getCategoryIcon(category.id)}
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">{category.name}</h3>
                        <div className="text-lg font-semibold text-primary-600">
                          {category.currency} {category.price.toLocaleString()} to join
                          {category.renewalPrice && (
                            <span className="text-sm text-gray-600 ml-2">
                              (Renewal: {category.currency} {category.renewalPrice.toLocaleString()})
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-600">{category.description}</p>
                  </div>
                  
                  <div className="lg:col-span-2">
                    {category.id === 'student' && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Who Can Join?</h4>
                        <ul className="space-y-2 text-gray-600 mb-4">
                          <li>• University undergraduates studying Computer Science or a related discipline</li>
                          <li>• Student identity card or other proof of current enrolment required</li>
                        </ul>
                      </div>
                    )}
                    
                    {category.id === 'affiliate' && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Who Can Join?</h4>
                        <ul className="space-y-2 text-gray-600 mb-4">
                          <li>• Graduates of non-computing or non-cybersecurity disciplines</li>
                          <li>• Minimum five years of post-NYSC professional experience</li>
                          <li>• First degree certificate, NYSC certificate and CV required</li>
                        </ul>
                      </div>
                    )}

                    {category.id === 'graduate' && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Who Can Join?</h4>
                        <ul className="space-y-2 text-gray-600 mb-4">
                          <li>• University graduates in Computer Science or a related discipline</li>
                          <li>• Recent graduates or applicants with up to one year of post-NYSC experience</li>
                          <li>• First degree certificate, NYSC certificate and CV required</li>
                        </ul>
                      </div>
                    )}
                    
                    {category.id === 'associate' && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Who Can Join?</h4>
                        <ul className="space-y-2 text-gray-600 mb-4">
                          <li>• University graduates in Computer Science or a related discipline</li>
                          <li>• Minimum three years of post-NYSC experience</li>
                          <li>• First degree certificate, NYSC certificate, CV and professional certifications required</li>
                        </ul>
                      </div>
                    )}
                    
                    {category.id === 'professional' && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Who Can Join?</h4>
                        <ul className="space-y-2 text-gray-600 mb-4">
                          <li>• University graduates in Computer Science or a related discipline</li>
                          <li>• Minimum five years of post-NYSC experience</li>
                          <li>• First degree certificate, NYSC certificate, CV and professional certifications required</li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Additional Information */}
      <div className="bg-white section-padding border-t border-gray-200">
        <div className="container-max">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Why Join CSEAN?
              </h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckIcon className="w-6 h-6 text-primary-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      Professional Development
                    </h3>
                    <p className="text-gray-600">
                      Access to exclusive training programs, workshops, and certification support.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckIcon className="w-6 h-6 text-primary-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      Industry Network
                    </h3>
                    <p className="text-gray-600">
                      Connect with cybersecurity professionals and industry leaders.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckIcon className="w-6 h-6 text-primary-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      Exclusive Resources
                    </h3>
                    <p className="text-gray-600">
                      Get access to research reports, best practices, and technical insights.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Ready to Get Started?
              </h2>
              <p className="text-gray-600 mb-6">
                Join thousands of cybersecurity professionals who trust CSEAN for their professional growth and development.
              </p>
              <div className="space-y-4">
                <a
                  href="/membership/application"
                  className="btn-primary w-full text-center inline-block"
                >
                  Start Your Application
                </a>
                <a
                  href="/membership/benefits"
                  className="btn-secondary w-full text-center inline-block"
                >
                  View All Benefits
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipCategoriesPage;
